// ── IP 관리 — IP 연결 목록 섹션 컴포넌트 ─────────────────────────────────────
import React from 'react';
import { toDateString } from '../../../utils/date';
import {
  tableWrapStyle, tableStyle, thStyle, paginationWrapStyle,
  mobileCardListStyle, mobileCardStyle, mobileCardHeadStyle,
  mobileInfoGridStyle, mobileInfoItemStyle, mobileInfoKeyStyle,
  mobileActionGridStyle, descEditWrapStyle, descEditWrapMobileStyle,
  compactDescInputStyle, compactBtnStyle, btn, dangerBtn, ipCodeStyle,
} from './styles';
import { stdCell, stdBadgeActive, stdBadgeMuted } from '../adminlayout/designStandard';
import { normalizeIp, formatIpWithAlias, isActiveRow } from './utils';

/**
 * IP 연결 목록 섹션 (설명 인라인 편집 포함)
 * Props:
 *   isMobile        — 모바일 여부
 *   pagedList       — 현재 페이지 IP 연결 행 목록
 *   list            — 전체 IP 연결 목록 (건수 확인용)
 *   currentPage     — 현재 페이지
 *   totalPages      — 전체 페이지 수
 *   aliasByIp       — ip → 별칭 Map
 *   descDraftById   — id → 설명 드래프트 map
 *   descBusyId      — 설명 저장 중인 row.id (null이면 없음)
 *   onDescChange    — 설명 입력 변경 콜백 (id, value) => void
 *   onSaveDesc      — 설명 저장 콜백 (row) => void
 *   onEdit          — IP 주소 수정 창 열기 (row) => void (2026-10-29)
 *   onToggle        — 활성/비활성 토글 콜백 (row) => void
 *   onRemove        — 삭제 콜백 (row) => void
 *   onPageChange    — 페이지 변경 콜백 (page) => void
 */
export function IpListSection({
  isMobile, pagedList, list,
  currentPage, totalPages,
  aliasByIp, descDraftById, descBusyId,
  onDescChange, onSaveDesc, onEdit, onToggle, onRemove, onPageChange,
}) {
  return (
    <>
      <div style={tableWrapStyle}>
        <div style={{ fontWeight: 700, padding: '12px 12px 0 12px' }}>IP 연결 목록</div>

        {list.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', padding: 12 }}>데이터가 없습니다.</div>
        ) : isMobile ? (
          /* 모바일 카드 */
          <div style={{ padding: 12 }}>
            <div style={mobileCardListStyle}>
              {pagedList.map((row) => (
                <article key={row.id} style={mobileCardStyle}>
                  <div style={mobileCardHeadStyle}>
                    <code style={{ display: 'inline-block', overflowWrap: 'anywhere', wordBreak: 'break-all' }}>
                      {formatIpWithAlias(row.ip_address, aliasByIp.get(normalizeIp(row.ip_address)) || row.label)}
                    </code>
                    <span style={{
                      ...(isActiveRow(row) ? stdBadgeActive : stdBadgeMuted),
                      whiteSpace: 'nowrap',
                    }}>
                      {isActiveRow(row) ? '활성' : '비활성'}
                    </span>
                  </div>
                  <div style={mobileInfoGridStyle}>
                    <div style={mobileInfoItemStyle}>
                      <span style={mobileInfoKeyStyle}>연결 대상</span>
                      <span>{row.user_name ? `${row.user_name} (${row.user_id})` : '공용(모든 직원)'}</span>
                    </div>
                    <div style={mobileInfoItemStyle}>
                      <span style={mobileInfoKeyStyle}>등록일</span>
                      <span>{toDateString(row.created_at || '') || '-'}</span>
                    </div>
                  </div>
                  {/* 설명 인라인 편집 */}
                  <div style={descEditWrapMobileStyle}>
                    <input style={{ ...compactDescInputStyle, height: 'auto', padding: '8px 10px' }}
                      value={descDraftById[row.id] ?? ''}
                      placeholder="설명 없음"
                      onChange={(e) => onDescChange(row.id, e.target.value)}
                    />
                    <button style={btn} disabled={descBusyId === row.id} onClick={() => onSaveDesc(row)}>
                      {descBusyId === row.id ? '저장 중...' : '설명 저장'}
                    </button>
                  </div>
                  <div style={{ ...mobileActionGridStyle, gridTemplateColumns: '1fr 1fr 1fr' }}>
                    <button style={btn} onClick={() => onEdit(row)}>수정</button>
                    <button style={btn} onClick={() => onToggle(row)}>{isActiveRow(row) ? '비활성' : '활성'}</button>
                    <button style={dangerBtn} onClick={() => onRemove(row)}>삭제</button>
                  </div>
                </article>
              ))}
            </div>
          </div>
        ) : (
          /* 데스크톱 테이블 */
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>IP 주소</th>
                <th style={thStyle}>설명</th>
                <th style={thStyle}>연결 대상</th>
                <th style={thStyle}>상태</th>
                <th style={thStyle}>등록일</th>
                <th style={thStyle}>관리</th>
              </tr>
            </thead>
            <tbody>
              {pagedList.map((row, rowIndex) => (
                <tr key={row.id}>
                  <td style={stdCell(rowIndex, pagedList.length)}>
                    <code style={ipCodeStyle}>
                      {formatIpWithAlias(row.ip_address, aliasByIp.get(normalizeIp(row.ip_address)) || row.label)}
                    </code>
                  </td>
                  <td style={stdCell(rowIndex, pagedList.length)}>
                    <div style={descEditWrapStyle}>
                      <input style={compactDescInputStyle}
                        value={descDraftById[row.id] ?? ''}
                        placeholder="설명 없음"
                        onChange={(e) => onDescChange(row.id, e.target.value)}
                      />
                      <button style={compactBtnStyle} disabled={descBusyId === row.id} onClick={() => onSaveDesc(row)}>
                        {descBusyId === row.id ? '저장중' : '저장'}
                      </button>
                    </div>
                  </td>
                  <td style={stdCell(rowIndex, pagedList.length)}>{row.user_name ? `${row.user_name} (${row.user_id})` : '공용(모든 직원)'}</td>
                  <td style={stdCell(rowIndex, pagedList.length)}>
                    <span style={isActiveRow(row) ? stdBadgeActive : stdBadgeMuted}>
                      {isActiveRow(row) ? '활성' : '비활성'}
                    </span>
                  </td>
                  <td style={stdCell(rowIndex, pagedList.length)}>{toDateString(row.created_at || '') || '-'}</td>
                  <td style={stdCell(rowIndex, pagedList.length)}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button style={btn} onClick={() => onEdit(row)}>수정</button>
                      <button style={btn} onClick={() => onToggle(row)}>{isActiveRow(row) ? '비활성' : '활성'}</button>
                      <button style={dangerBtn} onClick={() => onRemove(row)}>삭제</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 페이지네이션 */}
      {list.length > 0 && (
        <div style={paginationWrapStyle}>
          <button style={btn} disabled={currentPage <= 1} onClick={() => onPageChange(Math.max(1, currentPage - 1))}>이전</button>
          <span style={{ color: 'var(--text-muted)', fontWeight: 600 }}>{currentPage} / {totalPages}</span>
          <button style={btn} disabled={currentPage >= totalPages} onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}>다음</button>
        </div>
      )}
    </>
  );
}
